/**
 * Hook đơn giản để mở StoreItemSheet với bất kỳ item type nào
 * Thay thế cho useGoToOrderWebstore với cách sử dụng linh hoạt hơn
 */

import {
  useStoreItemSheet,
  type StoreContext,
} from '@/app/[locale]/(order)/components/store-item/store-item-sheet-context';
import { useCartStore } from '@/store/cart/cart-store-provider';
import type { MenuItem } from '@/types/item';

export interface OpenCartSheetOptions {
  /** Store ID */
  storeId: string;
  /** Page ID */
  pageId: string;
  /** Store slug for navigation */
  slug: string;
  /** Branch ID (optional) */
  branchId?: string;
  /** Tag để identify nguồn gốc (solomeal, search, recommendation, etc.) */
  tag?: string;
  /**
   * Metadata tùy chọn để tracking
   * ⚠️ LƯU Ý: Chỉ sử dụng flat object với string/number values (không nest)
   * GA giới hạn mỗi field max 100 ký tự
   * VD: { distance: 1500, rating: 4.5, store_name: "Tên quán" }
   */
  metadata?: Record<string, string | number | boolean>;
  /** Có clear cart nếu khác store không (default: true) */
  clearIfDifferentStore?: boolean;
}

export function useAddToCartSheet() {
  const { openSheet } = useStoreItemSheet();
  const setBranchId = useCartStore((state) => state.setBranchId);
  const clearCartIfDifferentStore = useCartStore(
    (state) => state.clearCartIfDifferentStore
  );

  /**
   * Mở sheet để add item vào cart
   * @param item MenuItem để hiển thị
   * @param options Các tùy chọn cho store context và tracking
   */
  const openCartSheet = (item: MenuItem, options: OpenCartSheetOptions) => {
    const {
      storeId,
      pageId,
      slug,
      branchId = '',
      tag,
      metadata,
      clearIfDifferentStore = true,
    } = options;

    // Clear cart if different store
    if (clearIfDifferentStore) {
      clearCartIfDifferentStore();
    }

    // Set branch and store ID
    setBranchId(branchId, storeId);

    // Set store context vào localStorage (for backward compatibility)
    if (typeof window !== 'undefined') {
      localStorage.setItem('ahafood.webstore_page_id', String(pageId));
      localStorage.setItem('ahafood.webstore_slug', String(slug));
    }

    // Prepare store context
    const storeContext: StoreContext = {
      storeId,
      pageId,
      slug,
      branchId,
      tag,
      metadata,
    };

    // Mở sheet
    openSheet(item, storeContext);
  };

  return { openCartSheet };
}
